export const CONVERSION_WHITELIST = new Set([
  'sign_up_success',
  'purchase',
  'first_deposit',
  'submit_deposit',
  'bank_verification_success',
  'login_success',
  'pwa_open',
  'pwa_first_open',
])

export const DEV_ONLY_CONVERSION_EVENTS = new Set(['activity_click'])

export const HIGH_FREQ_DENYLIST = new Set(['page_view', 'open_page', 'close_page', 'lobby_click', 'game_click', 'click', 'error_event', 'search'])

export const SECOND_ARG_EVENTS = new Set(['purchase', 'first_deposit', 'sign_up_success'])

export const resolveUserProperties = (profile = {}, registrationData = {}) => {
  const { uidSite, strategy, agentId } = registrationData

  return {
    level: profile.level || 0,
    strategy: profile.strategy || strategy || '',
    scope: profile.scope || '',
    topup: profile.topup || 0,
    verification_status: profile.verification_status || 0,
    uidSite: profile.uid_site || uidSite || '',
    user_tag_RFM: profile.user_tag || '',
    agent_id: profile.agent_id || agentId || '',
  }
}

export const shouldSendConversion = ({ event, isDevLike, gateTraceId }) => {
  const isWhitelisted = CONVERSION_WHITELIST.has(event) || (isDevLike && DEV_ONLY_CONVERSION_EVENTS.has(event))
  return isWhitelisted && !!gateTraceId
}

export const resolveConversionDispatch = ({ event, isDevLike, secondArg, opts, cookieTraceId }) => {
  const attribution = opts?.conversionAttribution
  const gateTraceId = attribution ? attribution.traceId : cookieTraceId
  if (!shouldSendConversion({ event, isDevLike, gateTraceId })) return null

  return {
    eventId: SECOND_ARG_EVENTS.has(event) ? secondArg?.eventID : undefined,
    attribution: attribution || undefined,
  }
}

// Single source of truth for event payload contracts.
// 規範：docs/standards/event-tracking-spec.md
//
// Consumed by:
//   - plugins/analytics.client.js        (runtime validation, dev-only warn)
//   - scripts/generate-event-manifest.cjs (static callsite scan，`npm run manifest:events`)
//
// Add an entry here when introducing a new event with a payload contract.
// Keep keys camelCase / snake_case 同 GA4 spec（不額外做 normalization）.
//
// Schema:
//   required: [...keys]      callsite 必須帶這些 key（非空字串、非 null/undefined）
//   deprecated: [...keys]    callsite 不得帶這些 key；contract 主動 reject
//
// 共用棄用清單（2026-Q2）— channel/sub_channel/affiliateCode 已全面廢棄，不帶入任何位置
const COMMON_DEPRECATED = ['channel', 'sub_channel', 'subChannel', 'affiliateCode', 'uidSite']

export const EVENT_CONTRACTS = {
  // ── 點擊類 ────────────────────────────────────────────────────────────
  click: {
    required: ['location', 'icon'],
    deprecated: COMMON_DEPRECATED,
  },
  click_deposit: {
    required: ['location', 'icon'],
    deprecated: COMMON_DEPRECATED,
  },
  click_register: {
    required: ['checkPoint', 'icon'], // icon: 'signup'
    deprecated: COMMON_DEPRECATED,
  },
  click_otp_verify: {
    required: ['checkPoint', 'icon'], // icon: 'verify'
    deprecated: COMMON_DEPRECATED,
  },
  click_binding_bank: {
    required: ['icon'], // icon: 'confirm'
    deprecated: COMMON_DEPRECATED,
  },
  click_promo_code: {
    required: ['icon'], // icon: 'confirm'
    deprecated: COMMON_DEPRECATED,
  },
  click_free_credit: {
    required: ['activityName', 'icon'],
    deprecated: COMMON_DEPRECATED,
  },

  // ── 領取完成（success 信號，與 click_free_credit 配對做 funnel） ─────
  // 跟 click_free_credit 的差別：
  //   click_free_credit = 使用者點下「領取」按鈕（intent，可能後面驗證失敗）
  //   claim_reward      = 後端確認領取成功（completion）
  // BI 可在 dashboard 看 intent → completion 漏斗轉換率。
  claim_reward: {
    required: ['activityName', 'claim_type', 'icon'], // claim_type: 'single' | 'batch'; icon: 'claim'
    deprecated: COMMON_DEPRECATED,
  },
  claim_free_game: {
    required: ['activityName', 'gameName', 'icon'], // icon: 'claim'
    deprecated: COMMON_DEPRECATED,
  },

  // ── 大廳 / 遊戲入口 ────────────────────────────────────────────────
  lobby_click: {
    required: ['location', 'icon'],
    deprecated: COMMON_DEPRECATED,
  },
  game_click: {
    required: ['location', 'provider', 'gameName', 'icon'],
    // 2026-Q2：gameId 棄用，改用 gameName
    deprecated: [...COMMON_DEPRECATED, 'gameId'],
  },

  // ── 活動 ─────────────────────────────────────────────────────────────
  activity_click: {
    required: ['location', 'template', 'activityName', 'icon'], // icon: `${template}|${activityName}`
    deprecated: COMMON_DEPRECATED,
  },

  // ── 活動詳情 ─────────────────────────────────────────────────────────────
  activity_detail: {
    required: ['location', 'template', 'activityNo', 'activityName', 'icon'], // icon: 'detail'
    deprecated: COMMON_DEPRECATED,
  },

  // ── 認證 / 註冊 ──────────────────────────────────────────────────────
  sign_up_success: {
    // eventID 不在 payload 內，由 callsite 透過 $analytics.push(payload, {eventID:'...'})
    // 第二個參數傳入（dispatcher 直接傳給 $fb.query 做 FB Pixel 去重）。
    // SECOND_ARG_EVENTS 集合追蹤需要第二個參數的事件名。
    required: ['checkPoint'],
    deprecated: COMMON_DEPRECATED,
  },
  login_success: {
    required: ['checkPoint', 'method'],
    deprecated: COMMON_DEPRECATED,
  },
  otp_verification_success: {
    required: ['checkPoint'],
    deprecated: COMMON_DEPRECATED,
  },
  bank_verification_success: {
    required: [],
    deprecated: COMMON_DEPRECATED,
  },

  // ── 存款流程 ─────────────────────────────────────────────────────────
  submit_deposit: {
    required: ['currency', 'value', 'page_title', 'icon'], // icon: 'submit'
    deprecated: COMMON_DEPRECATED,
  },
  purchase: {
    // eventID 由第二個 push 參數傳入（FB Pixel 去重），見 SECOND_ARG_EVENTS。
    required: ['transaction_id', 'value', 'currency', 'paymentMethod', 'isFirst'],
    deprecated: COMMON_DEPRECATED,
  },
  first_deposit: {
    // 同 purchase，eventID 由第二個 push 參數傳入。
    required: ['transaction_id', 'value', 'currency', 'paymentMethod', 'isFirst'],
    deprecated: COMMON_DEPRECATED,
  },

  // ── 搜尋 ─────────────────────────────────────────────────────────────
  search: {
    required: ['searchTag', 'icon'], // icon: 同 searchTag 值
    deprecated: COMMON_DEPRECATED,
  },

  // ── 頁面生命週期 ─────────────────────────────────────────────────────
  open_page: {
    required: ['location', 'page_title'],
    deprecated: COMMON_DEPRECATED,
  },
  // page_view: 主要由 dispatcher trackPageView() router hook 在 SPA 路由切換時直送 $gtag
  // （不走 contract validation）；少數 callsite（GameList / Side 系列）補送路由 hook 涵蓋不到的
  // 館別切換場景。各 tracker 也各自有 SPA pageview router hook（FB autoPageView /
  // Umami trackPageview / TikTok query('page') / Kwai track('pageView')）。contract 保留
  // 0 required 僅作為 event 名稱註冊以避免 spec doc orphan ref。詳見 spec §3.22。
  page_view: {
    required: [],
    deprecated: COMMON_DEPRECATED,
  },
  close_page: {
    required: ['page_title'],
    deprecated: COMMON_DEPRECATED,
  },

  // ── 錯誤 ─────────────────────────────────────────────────────────────
  error_event: {
    // page_title 由 dispatcher 自動補（從 page context 或 document.title），
    // callsite 負責 error_* 與 HTTP context 欄位
    required: ['error_message', 'error_message_userVisible', 'error_code', 'http_url', 'http_method', 'http_status'],
    deprecated: COMMON_DEPRECATED,
  },
}

export function validateEventPayload(event, payload) {
  const contract = EVENT_CONTRACTS[event]
  if (!contract) return { ok: true }
  for (const key of contract.required) {
    const v = payload && payload[key]
    if (v == null || v === '') {
      return { ok: false, reason: `event "${event}" requires non-empty "${key}"` }
    }
  }
  if (contract.deprecated && payload) {
    for (const key of contract.deprecated) {
      // 用 hasOwnProperty.call 避免把 prototype chain 上的屬性算進去（防 toString / constructor 等名字相撞）
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        return { ok: false, reason: `event "${event}" key "${key}" is deprecated and must not be sent` }
      }
    }
  }
  return { ok: true }
}

// Pure enrichment used by plugins/analytics.client.js. Extracted here so it can
// be unit-tested without bringing in Nuxt / store / window dependencies.
//
// 設計取捨：
//  - 自動補入「callsite 不可能有不同值」的 noise / 全域中繼欄位（page_title、
//    pwa_version、display_mode）。
//  - channel / sub_channel 已於 2026-Q2 全面棄用，dispatcher 不負責補入；contract
//    validation 會主動 reject 任何殘留 callsite 帶這兩個鍵的情況。
//  - callsite 已自帶同名欄位則不覆蓋（preserve override）。
//
// pwa_version + display_mode 設計理由：
//  - GA4 透過 gtag('config', GA_ID, { pwa_version, display_mode }) 一次設定即可，
//    其他 tracker（FB Pixel / TikTok / Kwai / Umami / Bridge）的 init API 都不支援
//    自訂維度（FB 限定 Advanced Matching schema），因此只能在每個事件 payload 帶。
//  - 為了 schema 一致性，所有 tracker（含 GA）都收到這兩個欄位（GA 會略多一份重複，
//    無害）。詳見 docs/standards/event-tracking-spec.md §1.5。
//
// page_title 設計理由：
//  - dispatcher 以 document.title 做 fallback；callsite 明確帶入則優先使用（spread 覆蓋）。
//  - error_event 的 page_title 由 dispatcher page context 補入，確保錯誤回報反映
//    使用者操作的功能脈絡，而非只依賴 document.title 快照。
export function enrichEventPayload(obj, metadata) {
  const safeObj = obj || {}
  const safeMeta = metadata || {}
  return {
    page_title: safeMeta.page_title || '',
    prev_page_title: safeMeta.prev_page_title || '',
    component_name: safeMeta.component_name || '',
    pwa_version: safeMeta.pwa_version || '',
    display_mode: safeMeta.display_mode || '',
    ...safeObj,
  }
}
